'use client';

import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { SelectComponent } from './select';

interface TimePickerProps {
  selectedDate: Date;
  onTimeChange: (time: Date) => void;
  label?: string;
  value?: string;
}

const getNextNearest30Min = (dateTime: Dayjs) => {
  const current = dayjs(dateTime);
  const minutes = current.minute();
  const next30Min = minutes < 30 ? 30 : 60;
  return current.minute(next30Min).second(0).millisecond(0);
};

function generateTimeSlots(selectedDate: Date) {
  const timeSlots = [];
  const now = dayjs();
  const isToday = dayjs(selectedDate).isSame(now, 'day');
  const startOfDay = isToday ? getNextNearest30Min(now) : dayjs(selectedDate).startOf('day');

  // Iterate in 30-minute intervals from the starting point to the end of the day
  for (
    let time = startOfDay;
    time.isBefore(dayjs(selectedDate).endOf('day'));
    time = time.add(30, 'minute')
  ) {
    timeSlots.push({
      label: time.format('h:mm A'),
      value: time.format('h:mm A'),
    });
  }
  return timeSlots;
}

function setTime(date: Date, timeString: string) {
  const [time, modifier] = timeString.split(' ');
  const [hours, minutes] = time.split(':').map(Number);
  let tempHours = hours;
  if (modifier === 'PM' && hours < 12) {
    tempHours += 12;
  }
  if (modifier === 'AM' && hours === 12) {
    tempHours = 0;
  }
  date.setHours(tempHours);
  date.setMinutes(minutes);
  return date;
}

export function TimePicker({ selectedDate, label, onTimeChange }: TimePickerProps) {
  const [timeSlots, setTimeSlots] = useState<Array<{ label: string; value: string }>>([]);
  useEffect(() => {
    setTimeSlots(generateTimeSlots(selectedDate));
    return () => {};
  }, [selectedDate]);

  return (
    <SelectComponent
      id='session-time-select'
      value={dayjs(selectedDate).format('h:mm A')}
      values={timeSlots}
      setValue={(val) => onTimeChange(setTime(selectedDate, val))}
      label={label}
      className='rounded-xl'
      clear={false}
    />
  );
}
